import React, { useState, useEffect, useCallback } from 'react';
import { UnitOfMeasure, UnitOfMeasureCreate, UnitOfMeasureUpdate, CurrencyLookupItem } from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { Checkbox } from './ui/checkbox.tsx';
import { Spinner } from './ui/spinner.tsx';

interface UnitOfMeasureFormProps {
  initialData?: UnitOfMeasure | null;
  onSubmit: (data: UnitOfMeasureCreate | UnitOfMeasureUpdate) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  currencies: CurrencyLookupItem[];
  lookupLoading: boolean;
}

const defaultFormData: UnitOfMeasureCreate = {
  name: '',
  units: 1,
  is_package: 0,
  created_by: 'tester',
};

const UnitOfMeasureForm: React.FC<UnitOfMeasureFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  currencies,
  lookupLoading,
}) => {
  const [formData, setFormData] = useState<UnitOfMeasureCreate | UnitOfMeasureUpdate>(
    initialData
      ? {
          name: initialData.name,
          units: initialData.units,
          is_package: initialData.is_package,
          updated_by: initialData.updated_by || 'tester',
        }
      : defaultFormData
  );

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        units: initialData.units,
        is_package: initialData.is_package,
        updated_by: initialData.updated_by || 'tester',
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [initialData]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  }, []);

  const handleSelectChange = useCallback((id: keyof (UnitOfMeasureCreate | UnitOfMeasureUpdate), value: string) => {
    setFormData((prev) => ({
      ...prev,
      [id]: value === '' ? null : parseInt(value, 10),
    }));
  }, []);

  const handleCheckboxChange = useCallback((checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      is_package: checked ? 1 : 0,
    }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  const isDisabled = isSubmitting || lookupLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">Name</Label>
        <Input
          id="name"
          type="text"
          required
          value={formData.name || ''}
          onChange={handleChange}
          disabled={isDisabled}
        />
      </div>
      <div>
        <Label htmlFor="units" className="block text-sm font-medium text-foreground mb-1">Number of Base Units</Label>
        <Input
          id="units"
          type="number"
          step="1"
          min="1"
          required
          value={formData.units || ''}
          onChange={handleChange}
          disabled={isDisabled}
        />
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_package"
          checked={formData.is_package === 1}
          onCheckedChange={handleCheckboxChange}
          disabled={isDisabled}
        />
        <Label htmlFor="is_package" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Is Package
        </Label>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isDisabled || formData.currency_id === null}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isDisabled}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default UnitOfMeasureForm;